import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/context/AuthContext";
import { LogoOrText } from "@/lib/logo";
import NotificationBell from "@/components/NotificationBell";
import ErrorBoundary from "@/components/ErrorBoundary";
import {
  LayoutDashboard, Building2, FileText, Wallet, FolderArchive,
  Hammer, MessageSquare, LogOut, Menu, X, TrendingUp, Users,
  Briefcase, Wallet2, Settings, Handshake,
} from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

// Cada item declara roles permitidos (mínimo)
const NAV_ITEMS = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", roles: ["OPERACIONAL", "GERENCIA", "ADMIN_MASTER"] },
  { to: "/admin/unidades", label: "Unidades", icon: Building2, testid: "nav-unidades", roles: ["OPERACIONAL", "GERENCIA", "ADMIN_MASTER"] },
  { to: "/admin/liquidez", label: "Liquidez", icon: TrendingUp, testid: "nav-liquidez", roles: ["GERENCIA", "ADMIN_MASTER"] },
  { to: "/admin/contratos", label: "Contratos", icon: FileText, testid: "nav-contratos", roles: ["GERENCIA", "ADMIN_MASTER"] },
  { to: "/admin/financeiro", label: "Financeiro", icon: Wallet, testid: "nav-financeiro", roles: ["GERENCIA", "ADMIN_MASTER"] },
  { to: "/admin/extrato", label: "Extrato", icon: Wallet2, testid: "nav-extrato", roles: ["GERENCIA", "ADMIN_MASTER"] },
  { to: "/admin/acordos", label: "Acordos", icon: Handshake, testid: "nav-acordos", roles: ["GERENCIA", "ADMIN_MASTER"] },
  { to: "/admin/dp", label: "DP & Folha", icon: Briefcase, testid: "nav-dp", roles: ["GERENCIA", "ADMIN_MASTER"] },
  { to: "/admin/documentos", label: "GED — Docs", icon: FolderArchive, testid: "nav-documentos", roles: ["GERENCIA", "ADMIN_MASTER"] },
  { to: "/admin/obras", label: "Obras", icon: Hammer, testid: "nav-obras", roles: ["OPERACIONAL", "GERENCIA", "ADMIN_MASTER"] },
  { to: "/admin/whatsapp", label: "WhatsApp", icon: MessageSquare, testid: "nav-whatsapp", roles: ["GERENCIA", "ADMIN_MASTER"] },
  { to: "/admin/config", label: "Configurações", icon: Settings, testid: "nav-config", roles: ["ADMIN_MASTER"] },
];

export default function AdminLayout() {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const items = NAV_ITEMS.filter((it) => it.roles.includes(admin?.role));

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 h-14 border-b border-border bg-background/95 backdrop-blur-xl flex items-center px-4 justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <LogoOrText className="h-7" textClass="font-heading font-black tracking-tighter truncate" />
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <NotificationBell />
          <button
            data-testid="admin-menu-toggle"
            onClick={() => setOpen(!open)}
            className="p-2 border border-border rounded-sm"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Sidebar */}
      <aside
        className={`${open ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0 fixed lg:sticky top-0 left-0 z-30 h-screen w-72
        bg-card border-r border-border flex flex-col transition-transform duration-200 ease-out`}
      >
        <div className="h-16 px-6 flex items-center gap-3 border-b border-border min-w-0">
          <LogoOrText className="h-9 max-w-[180px]" textClass="font-heading font-black text-lg tracking-tighter" />
          <div className="label-tag ml-auto">Admin</div>
        </div>

        <nav className="flex-1 overflow-y-auto py-4">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                data-testid={it.testid}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-6 py-3 border-l-2 transition-colors duration-150
                  ${isActive
                    ? "border-foreground bg-accent text-foreground font-semibold"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:bg-accent/50"}`
                }
              >
                <Icon size={16} strokeWidth={2} />
                <span className="text-sm">{it.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-border p-4 space-y-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 bg-secondary flex items-center justify-center rounded-sm flex-shrink-0">
              <Users size={14} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{admin?.name}</div>
              <div className="text-xs text-muted-foreground truncate">{admin?.email}</div>
              <Badge className="text-[9px] uppercase tracking-widest mt-1">{admin?.role}</Badge>
            </div>
          </div>
          <Button
            data-testid="admin-logout-btn"
            onClick={handleLogout}
            variant="outline"
            size="sm"
            className="w-full rounded-sm"
          >
            <LogOut size={14} className="mr-2" />
            Sair
          </Button>
        </div>
      </aside>

      <main className="flex-1 lg:ml-0 pt-14 lg:pt-0 min-w-0">
        <div className="hidden lg:flex sticky top-0 z-20 h-16 bg-background/90 backdrop-blur-xl border-b border-border items-center justify-end px-8 gap-3">
          <NotificationBell />
        </div>
        <div className="max-w-[1600px] mx-auto p-4 sm:p-6 lg:p-10">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </div>
      </main>

      {open && (
        <div
          className="lg:hidden fixed inset-0 z-20 bg-foreground/50 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        />
      )}
    </div>
  );
}
